import { ref, computed, triggerRef, readonly } from 'vue';

interface Identifiable {
  id: number;
}

export function useItemSelection<T extends Identifiable>() {
  const selectedIds = ref<Set<number>>(new Set());

  const isSelected = (id: number) => selectedIds.value.has(id);

  const toggleSelection = (id: number) => {
    if (selectedIds.value.has(id)) {
      selectedIds.value.delete(id);
    } else {
      selectedIds.value.add(id);
    }
    triggerRef(selectedIds);
  };

  const selectAll = (items: T[]) => {
    items.forEach((item) => selectedIds.value.add(item.id));
    triggerRef(selectedIds);
  };

  const deselectAll = () => {
    selectedIds.value.clear();
    triggerRef(selectedIds);
  };

  const toggleAll = (items: T[]) => {
    const allSelected = items.every((item) => selectedIds.value.has(item.id));
    if (allSelected) {
      deselectAll();
    } else {
      selectAll(items);
    }
  };

  const selectedCount = computed(() => selectedIds.value.size);
  const selectedItems = computed(() => Array.from(selectedIds.value));
  const hasSelected = computed(() => selectedIds.value.size > 0);

  return {
    selectedIds: readonly(selectedIds),
    selectedCount,
    selectedItems,
    hasSelected,
    isSelected,
    toggleSelection,
    selectAll,
    deselectAll,
    toggleAll,
  };
}
